import { createContext, useContext, useMemo, useCallback, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";

interface StoreSettings {
  id: string;
  store_name: string;
  logo_url: string | null;
  phone: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  accent_color: string | null;
  primary_font: string | null;
  secondary_font: string | null;
  settings_data: Record<string, any>;
}

interface StoreSEO {
  seo_titulo?: string;
  seo_meta_descricao?: string;
  seo_palavras_chave?: string;
  seo_sitemap?: boolean;
  seo_indexacao?: boolean;
}

interface StoreSettingsContextType {
  settings: StoreSettings;
  seo: StoreSEO;
  loading: boolean;
  refetch: () => Promise<void>;
}

const defaultSettings: StoreSettings = {
  id: "",
  store_name: "Minha Loja",
  logo_url: null,
  phone: null,
  primary_color: null,
  secondary_color: null,
  accent_color: null,
  primary_font: null,
  secondary_font: null,
  settings_data: {},
};

const defaultSEO: StoreSEO = {};

const StoreSettingsContext = createContext<StoreSettingsContextType>({
  settings: defaultSettings,
  seo: defaultSEO,
  loading: true,
  refetch: async () => {},
});

async function fetchStoreSettings(tenantId: string | null) {
  if (!tenantId) return null;

  const { data } = await supabase
    .from("store_settings")
    .select("id, store_name, logo_url, phone, primary_color, secondary_color, accent_color, primary_font, secondary_font, settings_data")
    .eq("tenant_id", tenantId)
    .limit(1)
    .maybeSingle();

  return data;
}

export function StoreSettingsProvider({ children }: { children: ReactNode }) {
  const { tenantId, loading: tenantLoading } = useTenant();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["store-settings", tenantId],
    queryFn: () => fetchStoreSettings(tenantId),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    enabled: !tenantLoading && !!tenantId,
  });

  const settings = useMemo<StoreSettings>(() => {
    if (!data) return defaultSettings;
    const sd = (data.settings_data as Record<string, any>) || {};
    return {
      id: data.id,
      store_name: data.store_name,
      logo_url: data.logo_url,
      phone: data.phone,
      primary_color: data.primary_color,
      secondary_color: data.secondary_color,
      accent_color: data.accent_color,
      primary_font: data.primary_font,
      secondary_font: data.secondary_font,
      settings_data: sd,
    };
  }, [data]);

  const seo = useMemo<StoreSEO>(() => {
    const sd = settings.settings_data;
    return {
      seo_titulo: sd.seo_titulo || undefined,
      seo_meta_descricao: sd.seo_meta_descricao || undefined,
      seo_palavras_chave: sd.seo_palavras_chave || undefined,
      seo_sitemap: sd.seo_sitemap,
      seo_indexacao: sd.seo_indexacao,
    };
  }, [settings]);

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["store-settings", tenantId] });
  }, [queryClient, tenantId]);

  const value = useMemo(
    () => ({ settings, seo, loading: isLoading || tenantLoading, refetch }),
    [settings, seo, isLoading, tenantLoading, refetch]
  );

  return (
    <StoreSettingsContext.Provider value={value}>
      {children}
    </StoreSettingsContext.Provider>
  );
}

export function useStoreSettings() {
  return useContext(StoreSettingsContext);
}
